import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { Description as DescriptionIcon } from '@mui/icons-material';

import type { Schema } from '../../services/api';
import SchemaDetailDrawer from '../common/SchemaDetailDrawer';
import UnifiedSchemaEditor from './UnifiedSchemaEditor';
import SourceFilesPreview from './SourceFilesPreview';

const jsonTextareaStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  fontFamily: 'monospace',
  fontSize: '0.9rem',
  padding: 12,
  boxSizing: 'border-box',
  overflow: 'auto',
  resize: 'none',
};

interface EditDraftDialogProps {
  open: boolean;
  onClose: () => void;
  selectedDraft: Schema | null;
  editMode: 'field' | 'json' | 'source';
  onEditModeChange: (mode: 'field' | 'json' | 'source') => void;
  editPayload: any;
  onEditPayloadChange: (payload: any) => void;
  editPayloadText: string;
  onEditPayloadTextChange: (text: string) => void;
  editError: string | null;
  onEditErrorChange: (error: string | null) => void;
  /** Kept for prop-compatibility; the new editor manages its own
   *  selection state internally. */
  expandedEditTables: Set<string>;
  onExpandedEditTablesChange: (tables: Set<string>) => void;
  onSave: (definition: any) => void;
  isSaving: boolean;
  onOpenRulesEditor: (tableName: string, columnName: string) => void;
}

/**
 * Returns true when the schema was created from an upload that persists raw
 * source file content — covers XML/XSD/JSON templates and SQL DDL uploads.
 */
function showSourceFilesTab(draft: Schema | null): boolean {
  if (!draft) return false;
  // Explicit source_type set at upload time (new schemas).
  if (draft.source_type === 'template') return true;
  if (draft.source_type === 'sql_ddl') return true;
  const def = draft.definition || {};
  // source_files key is stored by the backend for new uploads.
  if (def.source_files) return true;
  // template_summary is stored in the definition for template schemas.
  if (def.template_summary) return true;
  // After annotation the output_structure gains a format field.
  const fmt = def.output_structure?.format;
  if (fmt === 'xml' || fmt === 'xsd' || fmt === 'json') return true;
  // needs_interpretation flag is set by the template parser on raw uploads.
  if (def.needs_interpretation === true) return true;
  return false;
}

/**
 * Edit Schema drawer. Same drawer shell as the viewer; body switches
 * between a Field Editor (tree + level detail + inline catalogs), a
 * raw JSON textarea, and (for XML/XSD templates) a Source Files preview.
 */
const EditDraftDialog: React.FC<EditDraftDialogProps> = ({
  open,
  onClose,
  selectedDraft,
  editMode,
  onEditModeChange,
  editPayload,
  onEditPayloadChange,
  editPayloadText,
  onEditPayloadTextChange,
  editError,
  onEditErrorChange,
  onSave,
  isSaving,
  onOpenRulesEditor,
}) => {
  const canShowSourceTab = showSourceFilesTab(selectedDraft);

  const handleSave = () => {
    try {
      const finalDefinition = editMode === 'field' ? editPayload : JSON.parse(editPayloadText);
      onEditErrorChange(null);
      if (selectedDraft) onSave(finalDefinition);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Invalid definition';
      onEditErrorChange(message);
    }
  };

  const title = (
    <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
      <Typography variant="h6" fontWeight={600} noWrap>
        Edit Schema
      </Typography>
      <Tabs value={editMode} onChange={(_, v) => onEditModeChange(v)}>
        <Tab label="Field Editor" value="field" />
        <Tab label="JSON (Advanced)" value="json" />
        {canShowSourceTab && (
          <Tab
            label="Source Files"
            value="source"
            icon={<DescriptionIcon fontSize="small" />}
            iconPosition="start"
          />
        )}
      </Tabs>
    </Stack>
  );

  const footer = (
    <>
      <Button onClick={onClose} disabled={isSaving}>
        Cancel
      </Button>
      <Button variant="contained" disabled={isSaving || editMode === 'source'} onClick={handleSave}>
        {isSaving ? <CircularProgress size={20} /> : 'Save changes'}
      </Button>
    </>
  );

  return (
    <SchemaDetailDrawer
      open={open}
      onClose={onClose}
      title={title}
      footer={footer}
      disableClose={isSaving}
    >
      {editError && (
        <Box sx={{ p: 2, pb: 0 }}>
          <Alert severity="error">{editError}</Alert>
        </Box>
      )}
      {editMode === 'field' && editPayload && (
        <UnifiedSchemaEditor
          editPayload={editPayload}
          onChange={onEditPayloadChange}
          onOpenRulesEditor={onOpenRulesEditor}
        />
      )}
      {editMode === 'json' && (
        <Box sx={{ p: 2, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <Alert severity="info" sx={{ mb: 2, flexShrink: 0 }}>
            Advanced mode: edit the raw JSON definition. Switch back to Field Editor to use the
            tree + level detail view.
          </Alert>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <textarea
              style={jsonTextareaStyle}
              value={editPayloadText}
              onChange={(e) => onEditPayloadTextChange(e.target.value)}
            />
          </Box>
        </Box>
      )}
      {editMode === 'source' && (
        <SourceFilesPreview
          sourceFiles={editPayload?.source_files ?? selectedDraft?.definition?.source_files}
        />
      )}
    </SchemaDetailDrawer>
  );
};

export default EditDraftDialog;
